import React, { useState, useEffect, useRef } from "react";
import { useLocale } from "@/context/LocaleContext";
import { matchRoundDisplay } from "@/lib/matchRoundDisplay";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { FlagCircle } from "@/components/Flag";
import { TAB_MAIN_TITLE_CLASS } from "@/lib/tab-page-title";
import { kickoffToDate } from "@/lib/kickoffTime";
import { formatMatchTeamLabel } from "@/lib/knockoutPlaceholderDisplay";
import { isMatchTipInputLocked } from "@/lib/matchTipsLocked";
import { formatMatchScore } from "@/lib/matchScores";
import { MatchPenWinnerMark } from "@/components/MatchPenWinnerMark";
import { useMatchScoreDraft } from "@/lib/useMatchScoreDraft";
import { ZoomIn, ZoomOut, Maximize } from "lucide-react";
import { MatchRow } from "@/components/MatchRow";

function MatchBox({ m, bet, bets, save, teamsByCode, logoByCode = {}, allFixtures = [], knockoutUnlockUntil = null }) {
  const { lang, t } = useLocale();
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const matchKey = String(m.id ?? "");
  const resolvedBet = bet ?? bets?.[matchKey] ?? bets?.[m.id];
  const locked = isMatchTipInputLocked(m, allFixtures, knockoutUnlockUntil);
  const { home: h, setHome: sh, away: a, setAway: sa, onFocus, onBlur } = useMatchScoreDraft(
    resolvedBet,
    save,
    m.id,
    locked,
  );
  const displayScore = formatMatchScore(m, lang);
  const homeFull = m.home_code ? (teamsByCode[m.home_code] || m.home_code) : m.home_placeholder;
  const awayFull = m.away_code ? (teamsByCode[m.away_code] || m.away_code) : m.away_placeholder;
  const home_name = formatMatchTeamLabel({
    code: m.home_code,
    placeholder: m.home_code ? null : m.home_placeholder,
    teamName: teamsByCode[m.home_code],
    lang,
  }) || homeFull;
  const away_name = formatMatchTeamLabel({
    code: m.away_code,
    placeholder: m.away_code ? null : m.away_placeholder,
    teamName: teamsByCode[m.away_code],
    lang,
  }) || awayFull;
  const roundLine = matchRoundDisplay(m, t);
  const kickoff = kickoffToDate(m.kickoff);
  const kickoffLabel = kickoff.toLocaleString(loc, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="bracket-match" data-testid={`bracket-match-${m.id}`}>
      <div className="text-[12px] uppercase tracking-wider text-[var(--text-dim)] mb-2 font-mono truncate">
        {roundLine} · {kickoffLabel}
      </div>
      <div className="bracket-row">
        <div className="flex items-center gap-3 min-w-0">
          <FlagCircle code={m.home_code} size={32} logoUrl={logoByCode[m.home_code]}/>
          <span className="truncate text-base inline-flex items-center gap-1 min-w-0" title={homeFull}>
            <span className="truncate">{home_name}</span>
            <MatchPenWinnerMark match={m} teamCode={m.home_code} />
          </span>
        </div>
        <input className={`score-input ${locked?"locked":""}`} style={{width:52,height:42,fontSize:18,flexShrink:0}}
               type="number" min="0" disabled={locked} value={h} onChange={e=>sh(e.target.value)} onFocus={onFocus} onBlur={onBlur}/>
      </div>
      <div className="bracket-row">
        <div className="flex items-center gap-3 min-w-0">
          <FlagCircle code={m.away_code} size={32} logoUrl={logoByCode[m.away_code]}/>
          <span className="truncate text-base inline-flex items-center gap-1 min-w-0" title={awayFull}>
            <span className="truncate">{away_name}</span>
            <MatchPenWinnerMark match={m} teamCode={m.away_code} />
          </span>
        </div>
        <input className={`score-input ${locked?"locked":""}`} style={{width:52,height:42,fontSize:18,flexShrink:0}}
               type="number" min="0" disabled={locked} value={a} onChange={e=>sa(e.target.value)} onFocus={onFocus} onBlur={onBlur}/>
      </div>
      {(m.status === "FINISHED" || m.status === "LIVE") && displayScore && (
        <div className="mt-2 text-center text-xs font-mono">
          <span className="text-[var(--text-dim)]">{t("project.resultLabel")} </span>
          <span className="text-[var(--accent-2)] font-bold tracking-wide">
            {displayScore}
          </span>
          {m.status === "LIVE" && (
            <span className="ml-2 inline-flex items-center gap-1 text-danger">
              <span className="live-dot" />{t("project.matchLive")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Pair({ matches, intraGap, side, ...rest }) {
  const cls = `bracket-pair ${side === "right" ? "bracket-pair-right" : ""}`;
  return (
    <div className={cls} style={{ gap: intraGap }}>
      {matches.map(m => <MatchBox key={m.id} m={m} {...rest}/>)}
    </div>
  );
}

function PairedColumn({ pairs, intraGap, interPairGap, paddingTop = 0, side, ...rest }) {
  return (
    <div className="flex flex-col" style={{ gap: interPairGap, paddingTop }}>
      {pairs.map((pair, idx) => (
        <Pair key={idx} matches={pair} intraGap={intraGap} side={side} {...rest}/>
      ))}
    </div>
  );
}

function chunk2(arr) {
  const out = [];
  for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
  return out;
}

function BracketCanvas({ col1L, col1R, col2L, col2R, col3L, col3R, sfL, sfR, fin, tpp, props }) {
  const { lang, t } = useLocale();
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const wrapperApi = useRef(null);
  const [minScale, setMinScale] = useState(0.1);

  const isDesktop = React.useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(min-width: 640px)")?.matches ?? false;
  }, []);

  const fitToView = React.useCallback(() => {
    const inst = wrapperApi.current;
    if (!inst) return;
    const w = inst.instance?.wrapperComponent;
    const c = inst.instance?.contentComponent;
    if (!w || !c) return;
    const wb = w.getBoundingClientRect();
    const cw = c.offsetWidth || c.scrollWidth;
    const ch = c.offsetHeight || c.scrollHeight;
    if (!cw || !ch) return;
    const scale = Math.min(wb.width / cw, wb.height / ch) * 0.93;
    const x = (wb.width - cw * scale) / 2;
    const y = (wb.height - ch * scale) / 2;
    inst.setTransform(x, y, scale, 0);
    setMinScale(isDesktop() ? Math.max(scale * 0.98, 0.1) : 0.1);
  }, [isDesktop]);

  useEffect(() => {
    let cancelled = false;
    let ro = null;

    const attachAndFit = () => {
      if (cancelled) return;
      const inst = wrapperApi.current;
      const wrapper = inst?.instance?.wrapperComponent;
      const content = inst?.instance?.contentComponent;
      if (!wrapper || !content?.offsetWidth) {
        requestAnimationFrame(attachAndFit);
        return;
      }
      fitToView();
      if (!ro) {
        ro = new ResizeObserver(() => fitToView());
        ro.observe(wrapper);
      }
    };

    attachAndFit();
    const tmr = setTimeout(attachAndFit, 100);

    return () => {
      cancelled = true;
      clearTimeout(tmr);
      ro?.disconnect();
    };
  }, [fitToView, col1L, col1R, col2L, col2R, col3L, col3R, sfL, sfR, fin, tpp]);

  return (
    <TransformWrapper ref={wrapperApi}
                      minScale={minScale} maxScale={2.5} initialScale={1}
                      centerOnInit limitToBounds
                      centerZoomedOut
                      disablePadding
                      wheel={{ step: 0.003, smoothStep: 0.0015 }}
                      doubleClick={{ disabled: true }}
                      panning={{ velocityDisabled: true }}
                      alignmentAnimation={{ sizeX: 12, sizeY: 12 }}>
      {({ zoomIn, zoomOut }) => (
        <>
          <div className="absolute z-20 right-4 top-4 flex flex-col gap-2">
            <button data-testid="bracket-zoom-in" onClick={()=>zoomIn()} className="btn-ghost p-2"><ZoomIn size={16}/></button>
            <button data-testid="bracket-zoom-out" onClick={()=>zoomOut()} className="btn-ghost p-2"><ZoomOut size={16}/></button>
            <button data-testid="bracket-reset" onClick={fitToView} className="btn-ghost p-2"><Maximize size={16}/></button>
          </div>
          <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "max-content", height: "max-content" }}>
            <div className="flex items-start justify-center gap-12 p-12">
              <PairedColumn pairs={col1L} intraGap={12} interPairGap={50} side="left" {...props}/>
              <PairedColumn pairs={col2L} intraGap={237} interPairGap={237} paddingTop={93} side="left" {...props}/>
              <PairedColumn pairs={col3L} intraGap={649} interPairGap={0} paddingTop={299} side="left" {...props}/>
              <div style={{ paddingTop: 711 }}>
                {sfL.map(m => (
                  <div key={m.id} className="bracket-stub"><MatchBox m={m} {...props}/></div>
                ))}
              </div>
              <div className="flex flex-col items-center gap-6 px-2" style={{ paddingTop: 623 }}>
                <div className="text-center">
                  <div className="font-display text-5xl text-[var(--accent)]">{t("round.finalBanner")}</div>
                  {fin[0] && (
                    <div className="text-xs text-[var(--text-dim)]">
                      {new Date(fin[0].kickoff).toLocaleDateString(loc, { day: "numeric", month: "short" })}
                      {fin[0].venue ? ` · ${fin[0].venue}` : ""}
                    </div>
                  )}
                </div>
                {fin.map(m => <MatchBox key={m.id} m={m} {...props}/>)}
                {tpp.length > 0 && <>
                  <div className="text-xs uppercase mt-4 tracking-widest text-[var(--text-dim)]">{t("round.bronzeHeading")}</div>
                  {tpp.map(m => <MatchBox key={m.id} m={m} {...props}/>)}
                </>}
              </div>
              <div style={{ paddingTop: 711 }}>
                {sfR.map(m => (
                  <div key={m.id} className="bracket-stub bracket-stub-right"><MatchBox m={m} {...props}/></div>
                ))}
              </div>
              <PairedColumn pairs={col3R} intraGap={649} interPairGap={0} paddingTop={299} side="right" {...props}/>
              <PairedColumn pairs={col2R} intraGap={237} interPairGap={237} paddingTop={93} side="right" {...props}/>
              <PairedColumn pairs={col1R} intraGap={12} interPairGap={50} side="right" {...props}/>
            </div>
          </TransformComponent>
        </>
      )}
    </TransformWrapper>
  );
}

function BracketMatchesBelow({
  fixtures,
  allFixtures = [],
  bets,
  saveBet,
  teamsByCode,
  logoByCode,
  oddsByMatchId = {},
  oddsCsTop3ByMatchId = {},
  knockoutUnlockUntil = null,
}) {
  const { lang, t } = useLocale();
  const loc = lang === "en" ? "en-GB" : "sv-SE";

  const sections = React.useMemo(() => {
    const sorted = [...fixtures].sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff));
    const out = [];
    let label = null;
    let bucket = [];
    for (const m of sorted) {
      const d = new Date(m.kickoff).toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "short" });
      if (d !== label) {
        if (bucket.length) out.push({ label, matches: bucket });
        label = d;
        bucket = [m];
      } else {
        bucket.push(m);
      }
    }
    if (bucket.length) out.push({ label, matches: bucket });
    return out;
  }, [fixtures, loc]);

  return (
    <div className="mx-auto w-full min-w-0 max-w-full space-y-8 sm:max-w-3xl">
      <div className="flex flex-col gap-8 px-3 sm:px-0">
        {sections.map((sec, dayIdx) => (
          <div key={sec.label} className="min-w-0">
            {dayIdx > 0 && (
              <div className="mb-8 mt-4 h-1.5 w-full bg-[var(--accent)]" role="separator" aria-hidden />
            )}
            <div className="text-center text-[var(--text-dim)] tracking-widest uppercase text-sm sm:text-base mb-3">
              {sec.label}
            </div>
            <div className="flex flex-col gap-3 max-sm:gap-7">
              {sec.matches.map((m) => {
                const key = String(m.id ?? "");
                const bet = bets[key] ?? bets[m.id];
                const topCsRaw = oddsCsTop3ByMatchId[key] ?? oddsCsTop3ByMatchId[m.id] ?? null;
                const topScoresForTable =
                  Array.isArray(topCsRaw) && topCsRaw.length > 0 ? topCsRaw.slice(0, 4) : null;
                return (
                  <MatchRow
                    key={key}
                    m={m}
                    bet={bet}
                    save={saveBet}
                    teamsByCode={teamsByCode}
                    logoByCode={logoByCode}
                    fixtures={allFixtures}
                    oddsLines={oddsByMatchId[key] ?? oddsByMatchId[m.id] ?? null}
                    topScores={topScoresForTable}
                    knockoutUnlockUntil={knockoutUnlockUntil}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function byBracketOrder(a, b) {
  const oa = a.bracket_order ?? 999;
  const ob = b.bracket_order ?? 999;
  if (oa !== ob) return oa - ob;
  return String(a.id ?? "").localeCompare(String(b.id ?? ""));
}

export default function BracketView({
  fixtures,
  allFixtures = [],
  bets,
  saveBet,
  teamsByCode,
  logoByCode = {},
  oddsByMatchId = {},
  oddsCsTop3ByMatchId = {},
  knockoutUnlockUntil = null,
}) {
  const { t } = useLocale();
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'tree'

  const props = { bets, save: saveBet, teamsByCode, logoByCode, allFixtures, knockoutUnlockUntil };

  let col1L, col1R, col2L, col2R, col3L, col3R, sfL, sfR, fin, tpp;
  const r32 = fixtures.filter((m) => m.stage === "R32").sort(byBracketOrder);
  const r16 = fixtures.filter((m) => m.stage === "R16").sort(byBracketOrder);
  const qf = fixtures.filter((m) => m.stage === "QF").sort(byBracketOrder);
  const sf = fixtures.filter((m) => m.stage === "SF").sort(byBracketOrder);
  tpp = fixtures.filter((m) => m.stage === "TPP");
  fin = fixtures.filter((m) => m.stage === "FINAL");
  col1L = chunk2(r32.slice(0, 8));
  col1R = chunk2(r32.slice(8, 16));
  col2L = chunk2(r16.slice(0, 4));
  col2R = chunk2(r16.slice(4, 8));
  col3L = chunk2(qf.slice(0, 2));
  col3R = chunk2(qf.slice(2, 4));
  sfL = sf.slice(0, 1);
  sfR = sf.slice(1, 2);

  return (
    <div>
      <h2 className={`${TAB_MAIN_TITLE_CLASS} mb-4 px-3 sm:px-0`}>{t("project.tabBracket")}</h2>
      <div className="flex justify-center mb-6 px-3 sm:px-0">
        <div className="flex w-full max-w-md rounded-none overflow-hidden border border-[var(--line-2)] divide-x divide-[var(--line-2)]">
          <button
            type="button"
            data-testid="bracket-view-list"
            onClick={() => setViewMode("list")}
            className={`flex-1 px-5 py-3 text-base font-medium rounded-none border-0 ${
              viewMode === "list" ? "tab-active" : "bg-[var(--panel)]/50 text-[var(--text-dim)]"
            }`}
          >
            {t("project.bracketViewList")}
          </button>
          <button
            type="button"
            data-testid="bracket-view-tree"
            onClick={() => setViewMode("tree")}
            className={`flex-1 px-5 py-3 text-base font-medium rounded-none border-0 ${
              viewMode === "tree" ? "tab-active" : "bg-[var(--panel)]/50 text-[var(--text-dim)]"
            }`}
          >
            {t("project.bracketViewTree")}
          </button>
        </div>
      </div>
      {viewMode === "tree" ? (
        <div
          className="bracket-canvas-host rounded-none sm:rounded-2xl border border-[var(--line)] overflow-hidden relative"
          style={{ height: "75vh" }}
        >
          <BracketCanvas
            col1L={col1L}
            col1R={col1R}
            col2L={col2L}
            col2R={col2R}
            col3L={col3L}
            col3R={col3R}
            sfL={sfL}
            sfR={sfR}
            fin={fin}
            tpp={tpp}
            props={props}
          />
        </div>
      ) : (
        <BracketMatchesBelow
          fixtures={fixtures}
          allFixtures={allFixtures}
          bets={bets}
          saveBet={saveBet}
          teamsByCode={teamsByCode}
          logoByCode={logoByCode}
          oddsByMatchId={oddsByMatchId}
          oddsCsTop3ByMatchId={oddsCsTop3ByMatchId}
          knockoutUnlockUntil={knockoutUnlockUntil}
        />
      )}
    </div>
  );
}
